import React, { useState, useRef, useEffect } from 'react';
import logoImage from '../images/logo.svg'; // Update this path as needed
import auth from '../../shared/mobx/auth-state';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { scrollToHashAnchor } from '../../helpers/scrollToAnchor';

const dropDownNavs = [
  { text: "Dashboard", path: "/dashboard", access: ["user", "admin"] },
  { text: "My Profile", path: "/my-profile", access: ["user", "admin"] },
  { text: "Subscription Details", path: "/subscriptions", access: ["user"] },
  { text: "Subscription History", path: "/user-subscription-history", access: ["user"] },
  { text: "Logout", path: "/auth/login?preaction=logout", access: ["user", "admin"] },
];

const sectionNavItems = [
  { label: 'Home', hash: '#home' },
  { label: 'Benefits', hash: '#benefit' },
  { label: 'Reviews', hash: '#reviews' },
  { label: 'Pricing', hash: '#pricing' },
  { label: 'FAQs', hash: '#faq' },
  { label: 'Contact', hash: '#contact' },
];

interface NavbarProps {
  isIndex?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isIndex }) => {
  const [isIconOpen, setIconOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [pendingHash, setPendingHash] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const navMenuRef = useRef<HTMLDivElement>(null);
  const bodyOverflowRef = useRef<string>('');
  const isHomePage = typeof isIndex === 'boolean' ? isIndex : location.pathname === '/';

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !(dropdownRef.current as HTMLElement).contains(event.target as Node)) {
      setIconOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isIconOpen]);

  const [activeHash, setActiveHash] = useState(
    location.pathname === '/' ? location.hash || '#home' : ''
  );

  useEffect(() => {
    setActiveHash(location.pathname === '/' ? location.hash || '#home' : '');
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!isHomePage || isNavOpen || !location.hash) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollToHashAnchor(location.hash, { behavior: 'smooth' });
    }, 50);

    return () => window.clearTimeout(timer);
  }, [isHomePage, isNavOpen, location.hash]);

  const handleNavigation = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsNavOpen(false);
    setIconOpen(false);
    navigate(path);
  };

  const handleNavClick = (hash: string) => () => {
    setIsNavOpen(false);
    setIconOpen(false);
    setPendingHash(hash);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = Math.max(
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );

      const threshold = window.innerWidth <= 1024 ? 15 : 100;
      setIsScrolled(scrollTop >= threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle closing nav menu on outside click
  useEffect(() => {
    if (!isNavOpen) return;
    const handleClickOutsideNav = (event: MouseEvent) => {
      if (navMenuRef.current && !(navMenuRef.current as HTMLElement).contains(event.target as Node)) {
        setIsNavOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideNav);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideNav);
    };
  }, [isNavOpen]);

  useEffect(() => {
    if (window.innerWidth > 991) {
      return;
    }

    if (isNavOpen) {
      bodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = bodyOverflowRef.current;
    }

    return () => {
      if (!isNavOpen) {
        document.body.style.overflow = bodyOverflowRef.current;
      }
    };
  }, [isNavOpen]);

  useEffect(() => {
    if (!pendingHash || isNavOpen) {
      return;
    }

    const hash = pendingHash;
    setPendingHash(null);

    window.setTimeout(() => {
      if (isHomePage) {
        setActiveHash(hash);

        if (hash === '#home') {
          if (location.hash) {
            window.history.pushState(null, '', '/');
          }
          navigate('/');
          window.requestAnimationFrame(() => {
            scrollToHashAnchor(hash);
          });
          return;
        }

        if (location.hash !== hash) {
          window.history.pushState(null, '', hash);
        }
        window.requestAnimationFrame(() => {
          scrollToHashAnchor(hash);
        });
        return;
      }

      navigate(`/${hash}`);
    }, 0);
  }, [isNavOpen, pendingHash, isHomePage, location.hash, navigate]);

  return (
    <>
      <span id="up" />
      <header className={`header webtopdf-wrap ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-md-3 col-sm-12 col-12">
              <div className="logo">
                <a href="/">
                  <img src={logoImage} width="240px" height="60px" className="img-responsive" alt="Logo" />
                </a>
              </div>
            </div>
            <div className="col-md-9 col-sm-12 col-12 mobile-wraper">
              <nav className="navbar navbar-expand-lg justify-content-center">
                <button
                  className="navbar-toggler"
                  type="button"
                  aria-controls="navbarNav"
                  aria-expanded={isNavOpen}
                  aria-label="Toggle navigation"
                  onClick={() => setIsNavOpen((prev) => !prev)}
                >
                  <span className="navbar-toggler-icon"></span>
                </button>
                <div
                  className={`collapse navbar-collapse justify-content-center${isNavOpen ? ' show' : ''}`}
                  id="navbarNav"
                  ref={navMenuRef}
                >
                  <ul className="navbar-nav">
                    {sectionNavItems.map((item) => (
                      <li key={item.hash} className="nav-item">
                        <button
                          type="button"
                          className={`nav-link nav-link-button ${activeHash === item.hash ? 'active' : ''}`}
                          onClick={handleNavClick(item.hash)}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                    <li className="nav-item">
                      <a href="https://webs2pdf.com/blog/" className='nav-link'>Blog</a>
                    </li>
                    <ul className="login-menu">
                      {dropDownNavs.map(
                        (item) =>
                          item.access.includes(auth.activeRole) && (
                            <li key={item.text} className="nav-items">
                              <a href={item.path} className="nav-links" onClick={handleNavigation(item.path)}>
                                {item.text}
                              </a>
                            </li>
                          )
                      )}
                    </ul>
                    <div className="sign-btn mobile-show">
                      {auth.isLoggedIn() ? (
                        <button
                          ref={dropdownRef}
                          onClick={() => setIconOpen(!isIconOpen)}
                          className={`circle-button ${isIconOpen ? 'toggleUserIcon' : ''}`}
                          style={{ backgroundColor: auth.currentUser?.profileImage ? 'transparent' : undefined }}
                        >
                          {auth.currentUser?.profileImage ? (
                            <img
                              width="40"
                              height="40"
                              style={{ width: '40px', height: '40px', display: 'block', borderRadius: 'inherit' }}
                              src={auth.currentUser?.profileImage}
                              alt="Profile"
                            />
                          ) : (
                            <i className="fas fa-user user-icon"></i>
                          )}
                          {isIconOpen && (
                            <ul className="dropdown-menus">
                              {dropDownNavs.map(
                                (item) =>
                                  item.access.includes(auth.activeRole) && (
                                    <li key={item.text} className="dropdown-item">
                                      <a href={item.path} onClick={handleNavigation(item.path)}>
                                        {item.text}
                                      </a>
                                    </li>
                                  )
                              )}
                            </ul>
                          )}
                        </button>
                      ) : (
                        <Link className="nav-link" to="/auth/login">
                          Sign In
                        </Link>
                      )}
                    </div>
                  </ul>
                </div>
              </nav>
              <div className="sign-btn">
                {auth.isLoggedIn() ? (
                  <button
                    ref={dropdownRef}
                    onClick={() => setIconOpen(!isIconOpen)}
                    className={`circle-button ${isIconOpen ? 'toggleUserIcon' : ''}`}
                    style={{ backgroundColor: auth.currentUser?.profileImage ? 'transparent' : undefined }}
                  >
                    {auth.currentUser?.profileImage ? (
                      <img
                        width="40"
                        height="40"
                        style={{ width: '40px', height: '40px', display: 'block', borderRadius: 'inherit' }}
                        src={auth.currentUser?.profileImage}
                        alt="Profile"
                      />
                    ) : (
                      <i className="fas fa-user user-icon"></i>
                    )}
                    {isIconOpen && (
                      <ul className="dropdown-menus">
                        {dropDownNavs.map(
                          (item) =>
                            item.access.includes(auth.activeRole) && (
                              <li key={item.text} className="dropdown-item">
                                <a href={item.path} onClick={handleNavigation(item.path)}>
                                  {item.text}
                                </a>
                              </li>
                            )
                        )}
                      </ul>
                    )}
                  </button>
                ) : (
                  <Link className="nav-link" to="/auth/login">
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default observer(Navbar);
